import { formatearCRC } from '@adelante/shared';
import type { CostoPorCategoria } from '@adelante/shared';
import { CaretDown, Check } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';

/**
 * Dropdown multi-select de categorías de producto (item_category_code).
 *
 * Permite incluir/excluir categorías del treemap y del árbol de costos.
 * Ejemplo de uso: deseleccionar "Cuenta" para ver cómo se comporta el pastel
 * sin las cuentas contables.
 *
 * Convención de `seleccion`:
 * - `null` = TODAS las categorías incluidas (sin filtro).
 * - `Set`  = exactamente las categorías incluidas (puede estar vacío = ninguna).
 *
 * El componente es self-contained (sin dependencia de Popover): un botón que
 * abre un panel absoluto con checkboxes; cierra al hacer click afuera.
 */
interface FiltroCategoriasProps {
  categorias: CostoPorCategoria[];
  seleccion: Set<string> | null;
  onToggle: (code: string) => void;
  onTodas: () => void;
  onNinguna: () => void;
}

export function FiltroCategorias({
  categorias,
  seleccion,
  onToggle,
  onTodas,
  onNinguna,
}: FiltroCategoriasProps) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setAbierto(false);
      }
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  const incluida = (code: string) => seleccion === null || seleccion.has(code);
  const totalCount = categorias.length;
  const incluidasCount =
    seleccion === null
      ? totalCount
      : categorias.filter((c) => seleccion.has(c.itemCategoryCode)).length;
  const hayFiltro = seleccion !== null && incluidasCount !== totalCount;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className={`flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition ${
          hayFiltro
            ? 'border-blue-400 bg-blue-50 text-blue-700'
            : 'border-input bg-background text-foreground hover:bg-zinc-50'
        }`}
        title="Seleccionar qué categorías incluir en el gráfico y la tabla"
      >
        Categorías
        <span className="tabular-nums text-muted-foreground">
          ({incluidasCount}/{totalCount})
        </span>
        <CaretDown className="h-3 w-3" weight="bold" />
      </button>

      {abierto && (
        <div className="absolute right-0 z-50 mt-1 w-72 rounded-md border border-zinc-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2 text-xs">
            <span className="font-medium text-muted-foreground">
              {incluidasCount} de {totalCount} categorías
            </span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onTodas} className="text-blue-600 hover:underline">
                Todas
              </button>
              <span className="text-zinc-300">|</span>
              <button type="button" onClick={onNinguna} className="text-blue-600 hover:underline">
                Ninguna
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto py-1">
            {categorias.map((c) => {
              const checked = incluida(c.itemCategoryCode);
              return (
                <button
                  type="button"
                  key={c.itemCategoryCode}
                  onClick={() => onToggle(c.itemCategoryCode)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-zinc-50"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      checked
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-zinc-300 bg-white'
                    }`}
                  >
                    {checked && <Check className="h-3 w-3" weight="bold" />}
                  </span>
                  <span className="flex-1 truncate" title={c.itemCategoryCode}>
                    {c.itemCategoryCode}
                  </span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {formatearCRC(c.totalCostoLcy)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
